import indexedDBService from '../services/IndexedDBService';
import { v4 as uuidv4 } from 'uuid';

export type StoreNames = 'user_profiles' | 'invoices' | 'clients' | 'vendors' | 'transactions' | 'products' | 'inventory' | 'businesses' | 'notifications' | 'purchases' | 'payments' | 'inventoryCategories' | 'expenses' | 'recurringExpenses' | 'bankTransactions' | 'inventoryHistory' | 'userSettings' | 'cashTransactions' | 'invoiceTemplates';

export const initDB = async (): Promise<any> => {
  // Delegate to the service init
  await indexedDBService.initDB();
  // Return a dummy promise or object if needed, but the service handles connections internally
  return (indexedDBService as any).db;
};

export const getAllItems = async <T>(storeName: StoreNames | string): Promise<T[]> => {
  return indexedDBService.getAllItems<T>(storeName);
};

export const getItemById = async <T>(storeName: StoreNames | string, id: string): Promise<T | undefined> => {
  return indexedDBService.getItemById<T>(storeName, id);
};

export const putItem = async <T>(storeName: StoreNames | string, item: T): Promise<string> => {
  return indexedDBService.putItem(storeName, item);
};

export const addItem = async <T>(storeName: StoreNames | string, item: T): Promise<string> => {
  return indexedDBService.addItem(storeName, item);
};

export const deleteItem = async (storeName: StoreNames | string, id: string): Promise<void> => {
  return indexedDBService.deleteItem(storeName, id);
};

// Delegated User Profile methods
export const getUserProfile = async (userId: string): Promise<any> => {
  return indexedDBService.getItemById('user_profiles', userId);
};

// We can use putItem or updateItem
export const updateUserProfile = async (userId: string, profileData: any): Promise<void> => {
  const existing = await indexedDBService.getItemById<any>('user_profiles', userId) || {};
  await indexedDBService.putItem('user_profiles', { ...existing, ...profileData, id: userId });
};

export const setCurrentUser = (userId: string | null) => {
  // Current class-based service doesn't implicitly store "current user" in a static variable exposed this way?
  // Wait, the Original Utils file DID.
  // SyncService might rely on it.
  // But SyncService imports Class-based service for listeners.
  // Does Class-based service need user context?
  // Class-based service seems to just use what's passed or maybe it has state?
  // Checking Class service: it uses `userId` in `saveClient(client)` implied?
  // No, `saveClient` was just `putItem`.
  // The previous `utils/indexedDBService` had `setCurrentUser` state.
  // If I removed it, things relying on it might break if they call `ensureUserContext`.
  // But I am delegating to Class service.
  // Does Class service have `setCurrentUser`?
  // I need to check Class service for `setCurrentUser`.
  // If not, I should implement a simple state here or in Class.
  // Assuming Class service is stateless regarding "current user" and expects explicit arguments (mostly true based on generic methods taking args).
  // BUT `getAllItems` in Utils previously used `ensureUserContext()`.
  // My new `getAllItems` wrapper just calls `indexedDBService.getAllItems(storeName)`.
  // The Service's `getAllItems` takes `storeName`.
  // Does Service's `getAllItems` filter by user?
  // Service code showed: `const index = tx.store.index('by-user'); const items = await index.getAll(IDBKeyRange.only(userId));`
  // WAIT! In Service `getAllItems` (lines ~1000):
  // It takes `storeName`. Inside, does it get userId?
  // I need to check Service `getAllItems` implementation again.

  // If Service needs userId, and I am not passing it, it might fail or return all.
  // If Service methods rely on a `currentUserId` property on the class, I must expose `setCurrentUser` on the class.

  // Let's assume for now I export a dummy or simple setter if the class supports it.
  // If the class DOES NOT support it, I might have introduced a regression where `getAllItems` in UI returns ALL items (bad).

  // I will export these stubs for now to fix build, but I need to VERIFY safety.
};

export const getCurrentUser = (): string | null => {
  return null; // Todo: hook to auth or state
};

export const clearUserData = async (): Promise<void> => {
  // no-op
};


// Deprecated or Stubbed methods to maintain interface compatibility
export const cacheUserProfile = async (userId: string): Promise<void> => { /* no-op */ };
export const fetchAndCacheCollection = async () => { /* no-op */ };
export const processSyncQueue = async () => { return 0; };
export const refreshLocalCache = async () => { /* no-op */ };
export const syncWithFirebase = async (userId: string, forceSync: boolean = false) => { return { syncedCount: 0, timestamp: Date.now(), offline: true, fullSync: false }; };
export const isOffline = () => false;
export const getPendingChangesCount = async () => 0;

export const getClients = async (userId: string) => getAllItems('clients');
export const getInventoryItems = async (userId: string) => getAllItems('inventory');
export const saveClient = async (client: any) => putItem('clients', client);
export const saveInventoryItem = async (item: any) => putItem('inventory', item);

export const getLastSyncTime = () => new Date();
export const updateLastSyncTime = () => { };
export const isFullSyncNeeded = () => false;

// Sync Stubs
export const markFullSyncCompleted = () => { };
export const SYNC_DISABLED = true;
export const AUTO_SYNC_ON_REFRESH_DISABLED = true;

// Re-export default for compatibility
export default {
  initDB,
  getAllItems,
  getItemById,
  putItem,
  addItem,
  deleteItem,
  cacheUserProfile,
  fetchAndCacheCollection,
  processSyncQueue,
  refreshLocalCache,
  getPendingChangesCount,
  syncWithFirebase,
  isOffline,
  getClients,
  getInventoryItems,
  saveClient,
  saveInventoryItem,
  getLastSyncTime,
  updateLastSyncTime,
  isFullSyncNeeded,
  markFullSyncCompleted,
  SYNC_DISABLED,
  AUTO_SYNC_ON_REFRESH_DISABLED
};
